import React from "react";
import {action, observable} from 'mobx';
import {inject, observer} from 'mobx-react';

import PropTypes from "prop-types";
import {Link, withRouter} from "react-router-dom";

import logo from "../../images/logo/logo_line.svg";
import logoSymbol from "../../images/logo/logo_icon.svg";

import './Sidebar.scss'
import { isSuperAdmin } from "../../utils/auth";

// 메뉴 그루핑 — 그룹 사이에 구분선 (수정 2-4)
const MENU_GROUPS = [
  { key: 'groupA', names: ['review', 'widget', 'booster', 'orderBooster'] },
  { key: 'groupB', names: ['settings', 'design', 'blog', 'seo'] },
  { key: 'groupC', names: ['payment', 'board'] },
  { key: 'groupD', names: ['aoa'] },
  { key: 'groupE', names: ['aoaMonitor'] }
];
const LS_KEY_EXPANDED = 'orp_sidebar_expanded';

@inject(({ common }) => ({
  navItems: common.navItems,
  setPageInfo: common.setPageInfo,
  toggleFavorite: common.toggleFavorite,
  isFavorite: common.isFavorite,
  favorites: common.favorites
}))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@observer
class Sidebar extends React.Component {

  @observable teamUser = {};
  @observable isExpanded = false;
  @observable expandedSections = {};
  @observable hoverMainMenu = null;
  @observable hoverFlyoutTop = 56;
  @observable isMobileDrawerOpen = false; // 768px 이하 드로어
  @observable isMobileViewport = false; // 768px 이하면 항상 펼침 모드로 렌더
  hoverCloseTimer = null;

  // 모바일에서는 isExpanded 와 무관하게 펼침 UI
  get displayExpanded() {
    return this.isMobileViewport || this.isExpanded;
  }

  constructor(props) {
    super(props);
    this.state = {
      currentMainMenu: this.props.route.info.mainMenu,
      currentSubMenu: this.props.route.info.subMenu,
      navItems: this.props.navItems
    };
    this.isLogin = this.props.isLogin;
    this.teamUser = this.props.me;
    this.props.setPageInfo(this.props.route);

    try {
      const saved = localStorage.getItem(LS_KEY_EXPANDED);
      if (saved === '1') this.isExpanded = true;
    } catch(e) {}

    // 현재 active 1depth 자동 펼침 (수정 3-5)
    if (this.state.currentMainMenu) {
      this.expandedSections[this.state.currentMainMenu] = true;
    }
  }

  componentDidMount() {
    this.syncBodyClass();
    this._onKeyDown = (e) => { if (e.key === 'Escape') this.closeMobileDrawer(); };
    document.addEventListener('keydown', this._onKeyDown);
    // Header 햄버거 버튼이 디스패치하는 외부 이벤트로 드로어 토글
    this._onMobileToggle = () => this.toggleMobileDrawer();
    window.addEventListener('sidebar-mobile-toggle', this._onMobileToggle);
    // 뷰포트 폭 추적 (768px 이하면 모바일 모드)
    this._onResize = () => {
      this.isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 768;
    };
    this._onResize();
    window.addEventListener('resize', this._onResize);
  }
  componentWillUnmount() {
    if (this._onKeyDown) document.removeEventListener('keydown', this._onKeyDown);
    if (this._onMobileToggle) window.removeEventListener('sidebar-mobile-toggle', this._onMobileToggle);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.route && this.props.route && prevProps.route.info !== this.props.route.info) {
      this.setState({
        currentMainMenu: this.props.route.info.mainMenu,
        currentSubMenu: this.props.route.info.subMenu
      });
      // 페이지 이동 시 해당 1depth 아코디언 자동 열기
      if (this.props.route.info.mainMenu) {
        this.expandedSections[this.props.route.info.mainMenu] = true;
      }
      // 라우트 이동 시 모바일 드로어 닫기
      this.closeMobileDrawer();
    }
  }

  @action openMobileDrawer = () => { this.isMobileDrawerOpen = true; };
  @action closeMobileDrawer = () => { this.isMobileDrawerOpen = false; };
  @action toggleMobileDrawer = () => { this.isMobileDrawerOpen = !this.isMobileDrawerOpen; };

  syncBodyClass = () => {
    try {
      const wrap = document.querySelector('.wrap');
      if (wrap) {
        if (this.isExpanded) wrap.classList.add('sidebar-expanded');
        else wrap.classList.remove('sidebar-expanded');
      }
    } catch(e) {}
  };

  @action toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
    try { localStorage.setItem(LS_KEY_EXPANDED, this.isExpanded ? '1' : '0'); } catch(e) {}
    if (!this.isExpanded) this.hoverMainMenu = null;
    this.syncBodyClass();
  };

  @action toggleAccordion = (name) => {
    this.expandedSections[name] = !this.expandedSections[name];
  };

  // 축소 모드 호버 플라이아웃
  @action onHoverMenu = (name, e) => {
    if (this.isExpanded) return;
    if (this.hoverCloseTimer) { clearTimeout(this.hoverCloseTimer); this.hoverCloseTimer = null; }
    this.hoverMainMenu = name;
    if (e && e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      // 아이콘의 세로 위치에 맞춰 플라이아웃 정렬 (최상단 8px 여백 보장)
      this.hoverFlyoutTop = Math.max(8, Math.round(rect.top));
    }
  };
  @action onLeaveMenu = () => {
    if (this.isExpanded) return;
    // 수정 4-4: 150ms 딜레이로 hover bridge 안정성 확보
    this.hoverCloseTimer = setTimeout(() => { this.hoverMainMenu = null; }, 150);
  };
  @action onEnterFlyout = () => {
    if (this.hoverCloseTimer) { clearTimeout(this.hoverCloseTimer); this.hoverCloseTimer = null; }
  };

  isMenuVisible = (item) => {
    if (item.isHidden) return false;
    if (item.isShowSuperAdmin) {
      return isSuperAdmin(this.teamUser);
    }
    return true;
  };

  isSubmenuVisible = (sub) => {
    if (sub.isShowSuperAdmin) {
      return isSuperAdmin(this.teamUser);
    }
    if (sub.isLocalhost) return window.location.hostname === 'localhost';
    return true;
  };

  computeGroupBoundaries = (visibleNames) => {
    const boundaries = new Set();
    for (let i = 0; i < MENU_GROUPS.length - 1; i++) {
      const lastInGroup = [...MENU_GROUPS[i].names].reverse().find(n => visibleNames.includes(n));
      if (lastInGroup) boundaries.add(lastInGroup);
    }
    return boundaries;
  };

  renderFavoriteBtn = (item) => {
    if (!item.to) return null;
    const favs = this.props.favorites || [];
    const active = favs.some(f => f.to === item.to);
    return (
      <span className={'favorite-toggle' + (active ? ' active' : '')}
            onClick={(e) => { e.stopPropagation(); this.props.toggleFavorite(item); }}
            title={active ? '즐겨찾기 해제' : '즐겨찾기 추가'}>
        <i className="material-icons">{active ? 'star' : 'star_border'}</i>
      </span>
    );
  };

  renderSubmenuItems = (subs) => {
    const _this = this;
    return (
      <ul className="depth2">
        {subs.map((sub, i) => {
          if (!this.isSubmenuVisible(sub)) return null;
          const active = sub.name === _this.state.currentSubMenu;
          return (
            <li key={i} className={active ? 'on' : ''}>
              <div className="submenu-item-row">
                {sub.to
                  ? <Link to={sub.to}>{sub.label}</Link>
                  : <span className="submenu-label">{sub.label}</span>}
                {_this.renderFavoriteBtn(sub)}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  render() {
    const { navItems: items } = this.state;
    const visibleItems = items.filter(it => this.isMenuVisible(it));
    const visibleNames = visibleItems.map(it => it.name);
    const boundaries = this.computeGroupBoundaries(visibleNames);

    return (
      <>
      {/* 모바일 전용: 배경 오버레이 (햄버거는 Header 컴포넌트에 위치) */}
      {this.isMobileDrawerOpen && (
        <div className="aside-mobile-backdrop"
             onClick={this.closeMobileDrawer}
             aria-hidden="true"/>
      )}

      <div className={'aside-wrap' + (this.displayExpanded ? ' is-expanded' : ' is-collapsed') + (this.hoverMainMenu ? ' open-submenu' : '') + (this.isMobileDrawerOpen ? ' is-mobile-open' : '')}>
        <div className="aside-menu">

          {/* 로고 영역 (수정 1) */}
          <div className="aside-logo">
            {this.displayExpanded ? (
              <>
                <Link to="/" className="aside-logo__link aside-logo__link--expanded">
                  <img src={logo} alt="onreple" className="aside-logo__img" />
                </Link>
                <button type="button"
                        className="aside-logo__toggle aside-logo__toggle--expanded"
                        onClick={this.toggleExpanded}
                        title="사이드바 접기"
                        aria-label="사이드바 접기">
                  <i className="material-icons">chevron_left</i>
                </button>
              </>
            ) : (
              <Link to="/" className="aside-logo__link aside-logo__link--collapsed" title="대시보드">
                <img src={logoSymbol} alt="onreple" className="aside-logo__symbol-img" />
              </Link>
            )}
          </div>

          {/* 축소 모드 전용 토글 — 로고 아래 별도 행 (수정 1-4) */}
          {!this.displayExpanded && (
            <button type="button"
                    className="aside-logo__toggle aside-logo__toggle--collapsed"
                    onClick={this.toggleExpanded}
                    title="사이드바 펼치기"
                    aria-label="사이드바 펼치기">
              <i className="material-icons">chevron_right</i>
            </button>
          )}

          <ul className="depth1">
            {visibleItems.map((item, idx) => {
              const isActive = item.name === this.state.currentMainMenu;
              // 모든 대메뉴 토글(접기/펼치기) 가능 — 활성 메뉴는 expandedSections 로 자동 펼침
              const isAlwaysExpanded = false;
              const isExpanded = isAlwaysExpanded || !!this.expandedSections[item.name];
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const showBoundaryAfter = boundaries.has(item.name);

              return (
                <React.Fragment key={idx}>
                  <li className={'menu' + (isActive ? ' on' : '') + (this.hoverMainMenu === item.name ? ' is-hover' : '') + ((item.name === 'aoa' || item.name === 'aoaMonitor') ? ' menu-aoa' : '')}
                      onMouseEnter={(e) => this.onHoverMenu(item.name, e)}
                      onMouseLeave={() => this.onLeaveMenu()}>
                    {this.displayExpanded ? (
                      <div className="menu-row"
                           onClick={() => hasSubmenu && !isAlwaysExpanded && this.toggleAccordion(item.name)}>
                        {item.to && !hasSubmenu
                          ? <Link to={item.to} className="menu-link">
                              <i className="material-icons">{item.mIcon}</i>
                              <span className="menu-label">{item.label}</span>
                            </Link>
                          : <div className="menu-link">
                              <i className="material-icons">{item.mIcon}</i>
                              <span className="menu-label">{item.label}</span>
                            </div>
                        }
                        {hasSubmenu && !isAlwaysExpanded && (
                          // 수정 3-2: 화살표만, 200ms rotate
                          <i className={'material-icons chev ' + (isExpanded ? 'open' : '')}>chevron_right</i>
                        )}
                      </div>
                    ) : (
                      item.to && !hasSubmenu
                        ? <Link to={item.to} className="menu-link menu-link--collapsed" title={item.label}>
                            <i className="material-icons">{item.mIcon}</i>
                          </Link>
                        : <div className="menu-link menu-link--collapsed" title={item.label}>
                            <i className="material-icons">{item.mIcon}</i>
                          </div>
                    )}

                    {this.displayExpanded && hasSubmenu && (
                      <div className={'accordion-body' + (isExpanded ? ' open' : '')}>
                        {isExpanded && this.renderSubmenuItems(item.submenu)}
                      </div>
                    )}
                  </li>

                  {showBoundaryAfter && <li className="menu-group-divider" aria-hidden="true" />}
                </React.Fragment>
              );
            })}
          </ul>
        </div>

        {/* 축소 모드 플라이아웃 (수정 4) */}
        {!this.displayExpanded && this.hoverMainMenu && (
          <div className="aside-flyout"
               style={{top: this.hoverFlyoutTop, maxHeight: `calc(100vh - ${this.hoverFlyoutTop + 12}px)`}}
               onMouseEnter={this.onEnterFlyout}
               onMouseLeave={this.onLeaveMenu}>
            {visibleItems.filter(it => it.name === this.hoverMainMenu).map((item, i) => (
              <div key={i} className="aside-flyout__inner">
                <div className="aside-flyout__head">
                  <i className="material-icons">{item.mIcon}</i>
                  <span>{item.label}</span>
                </div>
                {item.submenu && item.submenu.length > 0
                  ? this.renderSubmenuItems(item.submenu)
                  : <div className="aside-flyout__empty">하위 메뉴가 없습니다.</div>
                }
              </div>
            ))}
          </div>
        )}
      </div>
      </>
    );
  }
}

Sidebar.propTypes = {
  route: PropTypes.object,
  hideLogoText: PropTypes.bool
};

Sidebar.defaultProps = {
  route: {},
  hideLogoText: false
};

export default withRouter(Sidebar);
